import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UsersThree,
  Plus,
  Trash,
  BellRinging,
  FloppyDisk,
  Lock,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { clearPermissionsCache } from "@/lib/permissions";

const Card = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`rounded-lg border border-zinc-800 bg-zinc-900 p-5 ${className}`}
  >
    {children}
  </div>
);

export default function UserGroups() {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [alerts, setAlerts] = useState({});
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);
  const [active, setActive] = useState("");

  const apply = (payload) => {
    setData(payload);
    setGroups(payload.groups);
    setAlerts(payload.alerts);
    setActive((cur) => cur || payload.groups[0]?.key || "");
  };

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/settings/user-groups");
      apply(payload);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load the groups",
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const group = groups.find((g) => g.key === active);

  const togglePerm = (perm, on) =>
    setGroups((list) =>
      list.map((g) =>
        g.key !== active
          ? g
          : {
              ...g,
              permissions: on
                ? [...new Set([...g.permissions, perm])]
                : g.permissions.filter((p) => p !== perm),
            },
      ),
    );

  const setAlertField = (key, patch) =>
    setAlerts((a) => ({ ...a, [key]: { ...a[key], ...patch } }));

  const toggleMobileKind = (kind, on) =>
    setGroups((list) =>
      list.map((g) =>
        g.key !== active
          ? g
          : {
              ...g,
              mobile_kinds: on
                ? [...new Set([...(g.mobile_kinds || []), kind])]
                : (g.mobile_kinds || []).filter((k) => k !== kind),
            },
      ),
    );

  const toggleList = (key, field, value, on) =>
    setAlertField(key, {
      [field]: on
        ? [...new Set([...(alerts[key]?.[field] || []), value])]
        : (alerts[key]?.[field] || []).filter((v) => v !== value),
    });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        groups: Object.fromEntries(
          groups.map((g) => [
            g.key,
            { label: g.label, permissions: g.permissions, service_categories: g.service_categories || [], mobile_kinds: g.mobile_kinds || [] },
          ]),
        ),
        alerts,
      };
      const { data: payload } = await api.put("/settings/user-groups", body);
      apply(payload);
      clearPermissionsCache();
      toast.success("Groups saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const addGroup = async () => {
    setBusy(true);
    try {
      const { data: payload } = await api.post("/settings/user-groups", adding);
      apply(payload);
      setActive(payload.key);
      setAdding(null);
      toast.success("Group added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add that group",
      );
    }
    setBusy(false);
  };

  const removeGroup = async (key) => {
    if (!window.confirm("Delete this group?")) return;
    try {
      const { data: payload } = await api.delete(
        `/settings/user-groups/${key}`,
      );
      apply(payload);
      setActive(payload.groups[0]?.key || "");
      toast.success("Group deleted");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not delete that group",
      );
    }
  };

  const isAdmin = active === "admin";

  return (
    <div className="grain min-h-screen">
      <AppHeader title="User groups" />
      <main className="relative z-10 pb-28 pt-6">
        <Card testid="user-groups-header-card">
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Access
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            User Groups
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-500">
            Pick which pages each group of crew can open and who gets the office
            alerts. Turning a page off hides it and blocks it on the server too.
          </p>
          <div
            className="mt-5 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4"
            data-testid="user-groups-actions"
          >
            <Button
              data-testid="group-save-button"
              disabled={busy || !data}
              onClick={save}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <FloppyDisk size={15} weight="bold" className="mr-2" />
              {busy ? "Saving…" : "Save changes"}
            </Button>
            <Button
              data-testid="group-add-button"
              variant="outline"
              onClick={() => setAdding({ label: "", copy_from: "sales" })}
              className="h-12 border-zinc-800 bg-zinc-950 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Add group
            </Button>
          </div>
        </Card>

        {!data && <p className="mt-6 text-sm text-zinc-500">Loading groups…</p>}

        {data && (
          <div className="mt-6 grid gap-4 lg:grid-cols-[16rem_1fr]">
            <div className="space-y-2" data-testid="group-list">
              {groups.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  data-testid={`group-tab-${g.key}`}
                  onClick={() => setActive(g.key)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors duration-150 ${
                    active === g.key
                      ? "border-indigo-500 bg-indigo-500/10"
                      : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                  }`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-indigo-400/25 bg-indigo-500/10 text-indigo-300">
                    {g.key === "admin" ? (
                      <Lock size={16} weight="bold" />
                    ) : (
                      <UsersThree size={16} weight="bold" />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold">
                      {g.label.split("—")[0].trim()}
                    </span>
                    <span className="block text-[11px] uppercase tracking-widest text-zinc-500">
                      {g.users} {g.users === 1 ? "person" : "people"}
                      {g.custom ? " · custom" : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {group && (
                <Card testid="group-pages-card">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Pages this group can see
                      </div>
                      {group.custom ? (
                        <Input
                          data-testid="group-label-input"
                          value={group.label}
                          onChange={(e) =>
                            setGroups((list) =>
                              list.map((g) =>
                                g.key === active
                                  ? { ...g, label: e.target.value }
                                  : g,
                              ),
                            )
                          }
                          className="mt-1 h-11 w-64 border-zinc-800 bg-zinc-950"
                        />
                      ) : (
                        <h2 className="mt-1 text-lg font-bold">
                          {group.label}
                        </h2>
                      )}
                    </div>
                    {group.custom && (
                      <Button
                        data-testid={`group-delete-${group.key}`}
                        variant="outline"
                        onClick={() => removeGroup(group.key)}
                        className="h-11 border-red-500/30 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10"
                      >
                        <Trash size={14} weight="bold" className="mr-2" />{" "}
                        Delete group
                      </Button>
                    )}
                  </div>

                  {isAdmin ? (
                    <p
                      className="mt-4 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-4 text-sm text-amber-200"
                      data-testid="group-admin-note"
                    >
                      Admin always keeps every page so you can never lock
                      yourself out.
                    </p>
                  ) : (
                    <div className="mt-4 space-y-5">
                      {data.sections.map((section) => (
                        <div key={section.key}>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">
                            {section.label}
                          </div>
                          <div className="mt-2 space-y-2">
                            {section.items.map((item) => {
                              const id = item.read || item.write;
                              return (
                                <div
                                  key={id}
                                  data-testid={`group-row-${id.replace(":", "-")}`}
                                  className="flex flex-wrap items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold">
                                      {item.label}
                                    </div>
                                    <div className="mt-0.5 text-xs text-zinc-500">
                                      {item.pages}
                                    </div>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-4">
                                    {item.read && (
                                      <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                                        <Checkbox
                                          data-testid={`group-${active}-${item.read.replace(":", "-")}`}
                                          checked={group.permissions.includes(
                                            item.read,
                                          )}
                                          onCheckedChange={(v) =>
                                            togglePerm(item.read, !!v)
                                          }
                                          className="h-5 w-5 border-zinc-600 data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500"
                                        />
                                        Can see
                                      </label>
                                    )}
                                    {item.write && (
                                      <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                                        <Checkbox
                                          data-testid={`group-${active}-${item.write.replace(":", "-")}`}
                                          checked={group.permissions.includes(
                                            item.write,
                                          )}
                                          onCheckedChange={(v) =>
                                            togglePerm(item.write, !!v)
                                          }
                                          className="h-5 w-5 border-zinc-600 data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500"
                                        />
                                        Can change
                                      </label>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!isAdmin && (
                    <div className="mt-6 border-t border-zinc-800 pt-4" data-testid="group-mobile-kinds">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#d8b26a]">
                        Mobile job types this group can see
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">
                        Leave everything unticked to show every job type in the mobile app. Tick types to limit the field app to just those — e.g. installers see only Install &amp; Service.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(data.mobile_kinds_catalog || []).map((k) => {
                          const on = (group.mobile_kinds || []).includes(k);
                          return (
                            <button
                              key={k}
                              type="button"
                              data-testid={`group-${active}-mobile-${k.toLowerCase()}`}
                              onClick={() => toggleMobileKind(k, !on)}
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                                on
                                  ? "border-[#c39e6d]/60 bg-[#c39e6d]/15 text-[#e2c88f]"
                                  : "border-zinc-700 bg-zinc-950 text-zinc-400 hover:border-zinc-600"
                              }`}
                            >
                              {k}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!isAdmin && (
                    <div className="mt-6 border-t border-zinc-800 pt-4" data-testid="group-services-visibility">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">
                        Services this group can see
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">
                        Leave everything unticked to show the full services catalog. Tick categories to limit what this group sees when quoting or browsing Services.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(data.service_categories || []).map((cat) => {
                          const on = (group.service_categories || []).includes(cat);
                          return (
                            <button
                              key={cat}
                              type="button"
                              data-testid={`group-service-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() =>
                                setGroups((list) =>
                                  list.map((g) =>
                                    g.key !== active
                                      ? g
                                      : {
                                          ...g,
                                          service_categories: on
                                            ? (g.service_categories || []).filter((c) => c !== cat)
                                            : [...(g.service_categories || []), cat],
                                        },
                                  ),
                                )
                              }
                              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                                on ? "border-indigo-500/60 bg-indigo-500/15 text-indigo-200" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100"
                              }`}
                            >
                              {cat}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              )}

              <Card testid="group-alerts-card">
                <div className="flex items-center gap-2">
                  <BellRinging
                    size={17}
                    weight="bold"
                    className="text-indigo-300"
                  />
                  <h2 className="text-lg font-bold">Alerts</h2>
                </div>
                <p className="mt-1 text-sm text-zinc-500">
                  Tick the groups and the individual crew who should hear about
                  each one. Nobody ticked means the alert is off.
                </p>

                <div className="mt-4 space-y-3">
                  {data.alert_catalog.map((alert) => {
                    const rule = alerts[alert.key] || {
                      groups: [],
                      users: [],
                      sms: false,
                    };
                    const off =
                      rule.groups.length === 0 && rule.users.length === 0;
                    return (
                      <div
                        key={alert.key}
                        data-testid={`alert-row-${alert.key}`}
                        className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-bold">
                              {alert.label}
                            </div>
                            <div className="mt-0.5 text-xs text-zinc-500">
                              {alert.detail}
                            </div>
                          </div>
                          <label className="flex shrink-0 items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                            Text as well
                            <Switch
                              data-testid={`alert-sms-${alert.key}`}
                              checked={!!rule.sms}
                              onCheckedChange={(v) =>
                                setAlertField(alert.key, { sms: v })
                              }
                            />
                          </label>
                        </div>

                        <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                          Groups
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {groups.map((g) => {
                            const on = rule.groups.includes(g.key);
                            return (
                              <button
                                key={g.key}
                                type="button"
                                data-testid={`alert-${alert.key}-group-${g.key}`}
                                onClick={() =>
                                  toggleList(alert.key, "groups", g.key, !on)
                                }
                                className={`h-8 rounded-full border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                                  on
                                    ? "border-indigo-500 bg-indigo-500/20 text-indigo-200"
                                    : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                                }`}
                              >
                                {g.label.split("—")[0].trim()}
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                          Individual crew
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {data.crew.map((member) => {
                            const on = rule.users.includes(member.id);
                            return (
                              <button
                                key={member.id}
                                type="button"
                                data-testid={`alert-${alert.key}-user-${member.id}`}
                                onClick={() =>
                                  toggleList(alert.key, "users", member.id, !on)
                                }
                                title={member.email}
                                className={`h-8 rounded-full border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                                  on
                                    ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"
                                    : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                                }`}
                              >
                                {member.name || member.email}
                                {rule.sms && !member.phone
                                  ? " · no mobile"
                                  : ""}
                              </button>
                            );
                          })}
                        </div>

                        {off && (
                          <p
                            className="mt-3 text-[11px] uppercase tracking-widest text-amber-400"
                            data-testid={`alert-off-${alert.key}`}
                          >
                            Nobody is set for this alert — it will not be sent
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          </div>
        )}
      </main>

      <Dialog open={!!adding} onOpenChange={(v) => !v && setAdding(null)}>
        <DialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="group-add-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base">Add a user group</DialogTitle>
          </DialogHeader>
          {adding && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Group name
                </Label>
                <Input
                  data-testid="group-add-name"
                  autoFocus
                  value={adding.label}
                  onChange={(e) =>
                    setAdding({ ...adding, label: e.target.value })
                  }
                  placeholder="Template crew"
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Start from
                </Label>
                <Select
                  value={adding.copy_from || ""}
                  onValueChange={(v) => setAdding({ ...adding, copy_from: v })}
                >
                  <SelectTrigger
                    data-testid="group-add-copy"
                    className="h-11 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue placeholder="Nothing — start empty" />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {groups.map((g) => (
                      <SelectItem key={g.key} value={g.key}>
                        A copy of {g.label.split("—")[0].trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="group-add-save"
                disabled={busy || !adding.label.trim()}
                onClick={addGroup}
                className="h-11 w-full bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                Create group
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
